import React from 'react'
import { EmailWrapper } from '../EmailWrapper'
import {
    Header,
    GeneralInfo,
    ClearanceCalc,
    DepartureOptions,
    Notes,
    getNotesForScenario,
    Graph,
    HighTidesTable,
    TidesAroundDeparture,
    SectionHeader,
} from '../sections'

/**
 * Check if this should render as a pilot scenario (3 or 4)
 * Only checks explicit scenario types - no legacy detection
 */
const isPilotScenario = calculationMethod => {
    return (
        calculationMethod === 'departure_range_pilot_desc' ||
        calculationMethod === 'departure_range_pilot_updown'
    )
}

/**
 * Email template for departure_range calculation methods (Scenarios 2, 3, 4)
 *
 * Scenario 2: Single section with departure options
 * Scenarios 3 & 4: Two sections (Sans pilote / Avec pilote)
 *
 * @param {object} props - Template props from prepareTemplateProps
 */
export const DepartureRangeTemplate = props => {
    const {
        boatName,
        dockLabel,
        analysisDate,
        calculationMethod,
        alias,
        boatDraft,
        tolerance,
        dockDepth,
        dockShoal,
        minTide,
        minTidePilot,
        endManoeuverHour,
        image,
        departureOptions,
        pilotDepartureOptions,
        pilotExtraTimeOptions,
        dualRangeOptions,
        pilotDualRangeOptions,
        departureWindowsWasEmpty,
        highTides,
        tidesAroundDeparture,
        imagePilot,
    } = props

    const hasPilotSections = isPilotScenario(calculationMethod)

    // Scenario 2: Single section (no pilot)
    if (!hasPilotSections) {
        return (
            <EmailWrapper>
                <Header boatName={boatName} dockLabel={dockLabel} />

                <GeneralInfo
                    boatName={boatName}
                    dockLabel={dockLabel}
                    analysisDate={analysisDate}
                    calculationMethod={calculationMethod}
                    alias={alias}
                />

                <ClearanceCalc
                    boatDraft={boatDraft}
                    tolerance={tolerance}
                    dockDepth={dockDepth}
                    dockLabel={dockLabel}
                    minTide={minTide}
                />

                <DepartureOptions
                    calculationMethod={calculationMethod}
                    departureOptions={departureOptions}
                />

                <Graph image={image} />
                <br />

                <Notes noteNumbers={getNotesForScenario(calculationMethod, false, dockLabel)} />

                {/* TODO: Remove after PDF attachment validation - tide tables now in Marees-Tides.pdf */}
                {/* <HighTidesTable tides={highTides} /> */}
                {/* <TidesAroundDeparture tides={tidesAroundDeparture} departureTime={endManoeuverHour} /> */}
            </EmailWrapper>
        )
    }

    // Scenarios 3 & 4: Two sections (Sans pilote / Avec pilote)
    // After DB column swap (migration 20251216000000):
    //   - departureOptions = from primary columns = shoal-based (sans pilote)
    //   - pilotDepartureOptions = from *_pilot columns = dock-based (avec pilote)
    return (
        <EmailWrapper>
            <Header boatName={boatName} dockLabel={dockLabel} />

            <GeneralInfo
                boatName={boatName}
                dockLabel={dockLabel}
                analysisDate={analysisDate}
                calculationMethod={calculationMethod}
                alias={alias}
            />

            {/* Section 1: Sans pilote lamaneur / Without harbour pilot */}
            {/* Captain alone must clear the shoal → uses SHOAL depth (more restrictive) */}
            <SectionHeader titleFr='Sans pilote lamaneur' titleEn='Without harbour pilot' />

            <ClearanceCalc
                boatDraft={boatDraft}
                tolerance={tolerance}
                dockDepth={dockShoal}
                dockLabel={dockLabel}
                minTide={minTidePilot}
            />

            <DepartureOptions
                calculationMethod={calculationMethod}
                departureOptions={departureOptions}
            />

            <Graph image={image} />

            {/* Section 2: Avec pilote lamaneur / With harbour pilot */}
            {/* LPA pilot can handle the shoal → uses DOCK depth (less restrictive) */}
            <SectionHeader
                titleFr='Avec pilote lamaneur'
                titleEn='With harbour pilot'
                superscripts='3,4'
            />

            <ClearanceCalc
                boatDraft={boatDraft}
                tolerance={tolerance}
                dockDepth={dockDepth}
                dockLabel={dockLabel}
                minTide={minTide}
                isPilotSection={true}
            />

            <DepartureOptions
                calculationMethod={calculationMethod}
                departureOptions={pilotDepartureOptions}
                dualRangeOptions={departureWindowsWasEmpty ? [] : pilotDualRangeOptions}
                extraTimeOptions={pilotExtraTimeOptions}
                showExtraTimesOnly={!departureWindowsWasEmpty}
            />

            <Graph image={imagePilot} />
            <br />

            <Notes noteNumbers={getNotesForScenario(calculationMethod, true, dockLabel)} />

            {/* TODO: Remove after PDF attachment validation - tide tables now in Marees-Tides.pdf */}
            {/* <HighTidesTable tides={highTides} /> */}
            {/* <TidesAroundDeparture tides={tidesAroundDeparture} departureTime={endManoeuverHour} /> */}
        </EmailWrapper>
    )
}
